import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    Divider, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField, Typography,
} from '@mui/material';
import { InsurancePolicyService } from '../../services/insurancePolicy.service';
import { StatutoryRegisterService } from '../../services/statutoryRegister.service';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { IInsurancePolicy } from '../../interfaces/insurance.interface';
import { formatDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import EnrollmentManagement from './EnrollmentManagement';

/**
 * Group-insurance policy screen (BLA §99), spec {@code docs/decisions/2026-09-08-group-insurance.md}
 * D1/D7 + REV 2 R4. Mirrors the {@code PayrollSettings} template: a form for the current/next
 * policy, an active-policy card showing the R4 EFFECTIVE status (never the bare flag), a history
 * list, and the D4 applicability chip — which reads the SAME roster-derived count the register
 * uses (R5), via {@code StatutoryRegisterService.getInsuranceRegister}, rather than a second
 * endpoint with its own count.
 */
const emptyForm = { insurerName: '', policyNumber: '', coverageAmountPerWorker: '', annualPremium: '', startDate: '', endDate: '' };

const statusColor = (status: string): 'success' | 'warning' | 'default' => {
    if (status === 'ACTIVE') return 'success';
    if (status === 'EXPIRED') return 'warning';
    return 'default';
};

const InsurancePolicyPanel: React.FC = () => {
    const institutionId = AuthService.getSelectedInstitution()!;
    const toast = useToast();
    // Every write below is INSURANCE_MANAGE server-side; INSURANCE_VIEW (Department Head) is read-only.
    const canManage = usePermission(PERMISSIONS.INSURANCE_MANAGE);
    const [activePolicy, setActivePolicy] = useState<IInsurancePolicy | null>(null);
    const [history, setHistory] = useState<IInsurancePolicy[]>([]);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [applicability, setApplicability] = useState<{ count: number; applies: boolean } | null>(null);
    const [deactivateTarget, setDeactivateTarget] = useState<IInsurancePolicy | null>(null);
    const [deactivateCount, setDeactivateCount] = useState<number | null>(null);

    const load = async () => {
        setLoading(true);
        try {
            const [active, list, register] = await Promise.all([
                InsurancePolicyService.getActive(institutionId),
                InsurancePolicyService.list(institutionId),
                StatutoryRegisterService.getInsuranceRegister(institutionId),
            ]);
            setActivePolicy(active);
            setHistory(list);
            setApplicability({ count: register.activeMemberCount, applies: register.applicabilityApplies });
        } catch (e) {
            toast.showApiError(e, 'Failed to load insurance policy');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId]);

    const handleCreate = async () => {
        setSaving(true);
        try {
            await InsurancePolicyService.create(institutionId, {
                insurerName: form.insurerName,
                policyNumber: form.policyNumber,
                coverageAmountPerWorker: Number(form.coverageAmountPerWorker),
                annualPremium: form.annualPremium === '' ? null : Number(form.annualPremium),
                startDate: form.startDate,
                endDate: form.endDate || null,
            });
            toast.showSuccess('Policy created and activated');
            setForm(emptyForm);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to create policy');
        } finally {
            setSaving(false);
        }
    };

    const handleActivate = async (id: string) => {
        try {
            await InsurancePolicyService.activate(institutionId, id);
            toast.showSuccess('Policy activated');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to activate policy');
        }
    };

    const openDeactivateConfirm = async (policy: IInsurancePolicy) => {
        setDeactivateTarget(policy);
        try {
            const count = await InsurancePolicyService.getOpenEnrollmentCount(institutionId, policy.id);
            setDeactivateCount(count);
        } catch (e) {
            setDeactivateCount(null);
        }
    };

    const confirmDeactivate = async () => {
        if (!deactivateTarget) return;
        try {
            await InsurancePolicyService.deactivate(institutionId, deactivateTarget.id);
            toast.showSuccess('Policy deactivated');
            setDeactivateTarget(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to deactivate policy');
        }
    };

    return (
        <Box data-help-page="insurance.policy.page">
            <Typography variant="h5" mb={2}>Group Insurance</Typography>

            {applicability && (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="insurance-applicability-chip" data-help="insurance.policy.applicability">
                    {applicability.applies
                        ? `Group insurance under BLA §99 is mandatory from 100 permanent workers, and you have ${applicability.count} active members, so it is likely to apply to you.`
                        : `Group insurance under BLA §99 is mandatory from 100 permanent workers; you have ${applicability.count} active members. You can still insure them voluntarily.`}
                </Alert>
            )}

            <Paper sx={{ p: 2, mb: 3 }} data-testid="insurance-active-policy-card" data-help-section="insurance.policy.active">
                <Typography variant="h6" mb={1}>Active Policy</Typography>
                {!loading && !activePolicy && <Typography color="text.secondary">No policy has been recorded for this institution.</Typography>}
                {activePolicy && (
                    <>
                        {activePolicy.effectiveStatus === 'EXPIRED' && (
                            <Alert severity="warning" sx={{ mb: 2 }} data-testid="insurance-expired-banner">
                                This policy's period ended {activePolicy.endDate ? formatDate(activePolicy.endDate) : ""} — there is no live cover today.
                            </Alert>
                        )}
                        <TableContainer>
                            <Table size="small">
                                <TableBody>
                                    <TableRow>
                                        <TableCell data-help="insurance.policy.active.status">Status</TableCell>
                                        <TableCell align="right">
                                            <Chip size="small" label={activePolicy.effectiveStatus} color={statusColor(activePolicy.effectiveStatus)} />
                                        </TableCell>
                                    </TableRow>
                                    <TableRow><TableCell data-help="insurance.policy.active.insurer">Insurer</TableCell><TableCell align="right">{activePolicy.insurerName}</TableCell></TableRow>
                                    <TableRow><TableCell data-help="insurance.policy.active.policyNumber">Policy Number</TableCell><TableCell align="right">{activePolicy.policyNumber}</TableCell></TableRow>
                                    <TableRow><TableCell data-help="insurance.policy.active.coverage">Coverage / Worker</TableCell><TableCell align="right">{formatMoney(activePolicy.coverageAmountPerWorker, 'BDT')}</TableCell></TableRow>
                                    <TableRow><TableCell data-help="insurance.policy.active.period">Period</TableCell><TableCell align="right">{formatDate(activePolicy.startDate)} — {activePolicy.endDate ? formatDate(activePolicy.endDate) : 'Open-ended'}</TableCell></TableRow>
                                    <TableRow><TableCell data-help="insurance.policy.active.openEnrollments">Open Enrollments</TableCell><TableCell align="right">{activePolicy.openEnrollmentCount}</TableCell></TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {canManage && (
                            <Box mt={2}>
                                <Button data-help="insurance.policy.action.deactivate" size="small" color="warning" onClick={() => openDeactivateConfirm(activePolicy)}>
                                    Deactivate
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            {activePolicy && <EnrollmentManagement institutionId={institutionId} policy={activePolicy} onEnrollmentChange={load} />}

            <Divider sx={{ my: 3 }} />

            {canManage && (
            <Paper sx={{ p: 2, mb: 3 }} data-help-section="insurance.policy.form">
                <Typography variant="h6" mb={2}>New / Renewal Policy</Typography>
                <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
                    <TextField data-help="insurance.policy.form.insurer" size="small" label="Insurer Name" value={form.insurerName}
                        onChange={e => setForm({ ...form, insurerName: e.target.value })} sx={{ width: 220 }} />
                    <TextField data-help="insurance.policy.form.policyNumber" size="small" label="Policy Number" value={form.policyNumber}
                        onChange={e => setForm({ ...form, policyNumber: e.target.value })} sx={{ width: 180 }} />
                    <TextField data-help="insurance.policy.form.coverage" size="small" label="Coverage / Worker" type="number" value={form.coverageAmountPerWorker}
                        onChange={e => setForm({ ...form, coverageAmountPerWorker: e.target.value })} sx={{ width: 180 }} />
                    <TextField data-help="insurance.policy.form.premium" size="small" label="Annual Premium" type="number" value={form.annualPremium}
                        onChange={e => setForm({ ...form, annualPremium: e.target.value })} sx={{ width: 180 }} />
                    <TextField data-help="insurance.policy.form.start" size="small" label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={form.startDate}
                        onChange={e => setForm({ ...form, startDate: e.target.value })} sx={{ width: 180 }} />
                    <TextField data-help="insurance.policy.form.end" size="small" label="End Date (optional)" type="date" InputLabelProps={{ shrink: true }} value={form.endDate}
                        onChange={e => setForm({ ...form, endDate: e.target.value })} sx={{ width: 180 }} />
                </Box>
                <Button data-help="insurance.policy.form.save" variant="contained" onClick={handleCreate} disabled={saving || !form.insurerName || !form.policyNumber || !form.coverageAmountPerWorker || !form.startDate}>
                    Save &amp; Activate
                </Button>
            </Paper>
            )}

            <Paper sx={{ p: 2 }} data-testid="insurance-policy-history" data-help-section="insurance.policy.history">
                <Typography variant="h6" mb={2}>History</Typography>
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            {history.length === 0 && (
                                <TableRow><TableCell colSpan={5}><Typography color="text.secondary">No policies yet.</Typography></TableCell></TableRow>
                            )}
                            {history.map(p => (
                                <TableRow key={p.id} hover>
                                    <TableCell data-help="insurance.policy.history.insurer">{p.insurerName}</TableCell>
                                    <TableCell data-help="insurance.policy.history.policyNumber">{p.policyNumber}</TableCell>
                                    <TableCell data-help="insurance.policy.history.period">{formatDate(p.startDate)} — {p.endDate ? formatDate(p.endDate) : 'Open-ended'}</TableCell>
                                    <TableCell data-help="insurance.policy.history.status"><Chip size="small" label={humanizeEnum(p.effectiveStatus)} color={statusColor(p.effectiveStatus)} /></TableCell>
                                    <TableCell align="right">
                                        {canManage && !p.active && (
                                            <Button data-help="insurance.policy.action.activate" size="small" onClick={() => handleActivate(p.id)}>Activate</Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={!!deactivateTarget} onClose={() => setDeactivateTarget(null)} PaperProps={{ 'data-help-page': 'insurance.deactivate.page' } as any}>
                <DialogTitle>Deactivate policy?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This will deactivate '{deactivateTarget?.policyNumber}' and end
                        {deactivateCount !== null ? ` ${deactivateCount}` : ''} open enrollment(s) under it today.
                        This cannot be undone from here.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeactivateTarget(null)} data-help="insurance.deactivate.cancel">Cancel</Button>
                    <Button color="warning" onClick={confirmDeactivate} data-help="insurance.deactivate.confirm">Deactivate</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default InsurancePolicyPanel;
